import React, { createContext, useContext, useEffect, useState } from 'react';
import { ApiService } from '../services/api';
import { AdminAuthToken } from '../services/api/models/AdminAuthToken';

interface AuthContextData {
  initialised: boolean;
  isLoggedIn?: boolean;
  loggingIn: boolean;
  login(username: string, password: string, storeToken: boolean): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider = (props: { children: React.ReactNode }) => {

  const [isLoggedIn, setLoggedIn] = useState<boolean>();
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const [initialised, setInitialised] = useState<boolean>(false);

  useEffect(() => {
    loadToken();
  }, []);

  const login = async (username: string, password: string, stayLoggedIn: boolean): Promise<void> => {
    try { 
      setLoggingIn(true);
      await ApiService.login({ userId: username, password: password });
      if (!ApiService.hasValidToken()) { 
        throw new Error('Not logged in'); //sanity check
      }
      saveToken(ApiService.token, stayLoggedIn);
      setLoggedIn(ApiService.hasValidToken());
    } catch (err:any) { 
      throw err;
    } finally { 
      setLoggingIn(false);
    }
  };

  const logout = async (): Promise<void> => {
    ApiService.setToken(null);
    saveToken(null);
    setLoggedIn(false);
    window.sessionStorage.clear();
    window.localStorage.clear();
  };

  const saveToken = (token: AdminAuthToken | null, persist:boolean = false) => {
    if (token) {
      if (persist) { 
        window.localStorage.setItem('jwt_token', JSON.stringify(token));
      } else { 
        window.sessionStorage.setItem('jwt_token', JSON.stringify(token));
      }
    } else {
      window.localStorage.removeItem('jwt_token');
      window.sessionStorage.removeItem('jwt_token');
    }
  };

  const loadToken = () => {
    try {
      const json = window.localStorage.getItem('jwt_token') || window.sessionStorage.getItem('jwt_token');
      if (json) {
        const token: AdminAuthToken = JSON.parse(json);
        ApiService.setToken(token);
        setLoggedIn(ApiService.hasValidToken());
      } else { 
        setLoggedIn(false);
      }
      setInitialised(true);
    } catch (err) {
      logout();
    }
  };

  return <AuthContext.Provider value={{ isLoggedIn, login, logout, loggingIn, initialised }}>{props.children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
